if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('.product-form__submit');
        this.submitButtonText = this.submitButton?.querySelector('span');

        this.buyNowButton = this.querySelector('[data-buy-now]');
        this.cartRequestPending = false;
        this.checkoutRedirectPending = false;

        if (document.querySelector('cart-drawer')) {
          this.submitButton?.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.onPageShow = () => {
          this.cartRequestPending = false;
          this.checkoutRedirectPending = false;

          this.submitButton?.removeAttribute('aria-disabled');
          this.buyNowButton?.removeAttribute('aria-disabled');
          this.submitButton?.classList.remove('loading');
          this.querySelector('.loading__spinner')?.classList.add('hidden');

          this.refreshCartUI();
        };
      }

      connectedCallback() {
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onPageShow);
      }

      async refreshCartUI() {
        try {
          if (!this.cart) return;
          if (
            typeof this.cart.getSectionsToRender !== 'function' ||
            typeof this.cart.renderContents !== 'function'
          ) {
            return;
          }

          const sectionIds = this.cart
            .getSectionsToRender()
            .map((section) => section.id);

          const cartResponse = await fetch(`${routes.cart_url}.js`, {
            headers: { Accept: 'application/json' },
          });

          if (!cartResponse.ok) return;

          const cartData = await cartResponse.json();

          const sectionsResponse = await fetch(
            `${window.location.pathname}?sections=${sectionIds.join(',')}`,
            {
              headers: { Accept: 'application/json' },
            }
          );

          if (!sectionsResponse.ok) return;

          const sectionsText = await sectionsResponse.text();

          let parsedSections = {};
          try {
            parsedSections = JSON.parse(sectionsText);
          } catch (e) {
            parsedSections = {};
          }

          this.cart.renderContents({
            ...cartData,
            sections: parsedSections,
          });

          if (this.cart.classList.contains('is-empty') && cartData.item_count > 0) {
            this.cart.classList.remove('is-empty');
          }
        } catch (e) {
          console.error('Cart refresh failed', e);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        if (
          this.cartRequestPending ||
          this.checkoutRedirectPending ||
          this.submitButton?.disabled ||
          this.variantIdInput.disabled ||
          this.submitButton?.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        const buyNow = evt.submitter?.hasAttribute('data-buy-now') === true;

        this.handleErrorMessage();
        this.error = false;
        this.cartRequestPending = true;

        this.submitButton?.setAttribute('aria-disabled', 'true');
        this.buyNowButton?.setAttribute('aria-disabled', 'true');
        this.submitButton?.classList.add('loading');

        const spinner = this.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');

        let linesUpdateDeferred = null;

        Promise.resolve()
          .then(() => {
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];

            const formData = new FormData(this.form);

            if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
              formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
              if (typeof this.cart.setActiveElement === 'function') {
                this.cart.setActiveElement(document.activeElement);
              }
            }

            config.body = formData;

            const variantId = formData.get('id');
            const quantity = parseInt(formData.get('quantity'), 10) || 1;

            linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

            return fetch(routes.cart_add_url, config).then(async (httpResponse) => {
              const response = await httpResponse.json();

              if (!httpResponse.ok && !response.status) {
                throw new Error(
                  response.description ||
                    response.message ||
                    'Cart request failed'
                );
              }

              return { response, variantId };
            });
          })
          .then(async ({ response, variantId }) => {
            if (response.status) {
              const message =
                response.description ||
                response.message ||
                'Unable to add this item to the cart.';

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || message,
                message: response.message,
              });

              this.handleErrorMessage(message);
              this.dispatchCartErrorEvent(message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(message));

              const soldOutMessage =
                this.submitButton?.querySelector('.sold-out-message');

              if (soldOutMessage) {
                this.submitButtonText?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              }

              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            if (buyNow) {
              this.checkoutRedirectPending = true;

              if (this.cart && typeof this.cart.renderContents === 'function') {
                try {
                  this.cart.renderContents(response);
                } catch (e) {
                  console.error('Cart render before checkout failed', e);
                }
              }

              const root = window.Shopify?.routes?.root || '/';

              setTimeout(() => {
                window.location.assign(`${root}checkout`);
              }, 150);

              return;
            }

            if (!this.cart) {
              window.location.assign(window.routes.cart_url);
              return;
            }

            const startMarker =
              CartPerformance.createStartingMarker('add:wait-for-subscribers');

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: variantId,
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker(
                'add:wait-for-subscribers',
                startMarker
              );
            });

            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');

            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.checkoutRedirectPending = false;

            this.handleErrorMessage(
              'Unable to confirm the cart update. Please check your cart before trying again.'
            );

            this.dispatchCartErrorEvent(
              e.message || 'Network error',
              'SERVICE_UNAVAILABLE'
            );

            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.cartRequestPending = false;
            this.submitButton?.classList.remove('loading');
            spinner?.classList.add('hidden');

            if (!this.checkoutRedirectPending) {
              if (!this.error) {
                this.submitButton?.removeAttribute('aria-disabled');
              }
              this.buyNowButton?.removeAttribute('aria-disabled');
            }

            if (
              this.cart &&
              this.cart.classList.contains('is-empty') &&
              !this.error
            ) {
              this.cart.classList.remove('is-empty');
            }

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            '.product-form__error-message'
          );

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton?.setAttribute('disabled', 'disabled');
          this.buyNowButton?.setAttribute('disabled', 'disabled');

          if (text) {
            if (this.submitButtonText) this.submitButtonText.textContent = text;
            const buyNowText = this.buyNowButton?.querySelector('span');
            if (buyNowText) buyNowText.textContent = text;
          }
        } else {
          this.submitButton?.removeAttribute('disabled');
          this.buyNowButton?.removeAttribute('disabled');

          if (this.submitButtonText) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }

          const buyNowText = this.buyNowButton?.querySelector('span');

          if (buyNowText) {
            buyNowText.textContent =
              this.buyNowButton?.dataset.defaultLabel || 'Buy it now';
          }
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );

        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;

        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) {
              return deferred.reject(
                new Error('Missing currency in cart response')
              );
            }

            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;

        this.dispatchEvent(
          new CartErrorEvent({ error: message, code })
        );
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
